// Core data client: Supabase-first with seeded fallback.
// Every module service is built on top of `crudFor(table, seedRows)`.

import { supabase } from "@/lib/supabaseClient";
import * as fallback from "./fallbackStore";

let liveStatus = null; // null = unknown, true = Supabase tables exist, false = fallback mode
let probePromise = null; // dedupes parallel probes — only ONE network check runs per session

export async function checkSupabaseReady() {
  if (liveStatus !== null) return liveStatus;
  if (!probePromise) {
    probePromise = supabase
      .from("site_content")
      .select("key")
      .limit(1)
      .then(({ error }) => {
        liveStatus = !error;
        return liveStatus;
      })
      .catch(() => {
        liveStatus = false;
        return false;
      });
  }
  return probePromise;
}

export function isLive() {
  return liveStatus === true;
}

/**
 * Generic CRUD for a table with seeded fallback.
 * `build` lets callers add filters/sorting on the Supabase query.
 */
export function crudFor(table, seedRows) {
  return {
    table,
    seedRows,

    async list(build) {
      const live = await checkSupabaseReady();
      if (live) {
        let query = supabase.from(table).select("*");
        if (build) query = build(query);
        const { data, error } = await query;
        if (!error && data) return data;
      }
      return fallback.getRows(table, seedRows);
    },

    async getBy(field, value) {
      const live = await checkSupabaseReady();
      if (live) {
        const { data, error } = await supabase.from(table).select("*").eq(field, value).maybeSingle();
        if (!error && data) return data;
        if (!error) return null;
      }
      return fallback.getRows(table, seedRows).find((r) => r[field] === value) || null;
    },

    async create(row) {
      const live = await checkSupabaseReady();
      if (live) {
        const { data, error } = await supabase.from(table).insert(row).select().single();
        if (error) throw new Error(error.message);
        return data;
      }
      return fallback.addRow(table, seedRows, row);
    },

    async update(id, patch) {
      const live = await checkSupabaseReady();
      if (live) {
        const { data, error } = await supabase.from(table).update(patch).eq("id", id).select().single();
        if (error) throw new Error(error.message);
        return data;
      }
      return fallback.updateRow(table, seedRows, id, patch);
    },

    async remove(id) {
      const live = await checkSupabaseReady();
      if (live) {
        const { error } = await supabase.from(table).delete().eq("id", id);
        if (error) throw new Error(error.message);
        return true;
      }
      fallback.removeRow(table, seedRows, id);
      return true;
    },
  };
}
